/**
 * Modal/diyalog odak sözleşmesi.
 *
 * - Açılışta odağı diyaloğun içine taşır (varsa `initialFocusRef`).
 * - Tab/Shift+Tab odağını diyalog içinde döndürür.
 * - Escape ile kapatır.
 * - Kapanışta odağı açan öğeye geri verir.
 *
 * Bu davranış her modalda kopyalanmaz; `role="dialog"` + `aria-modal`
 * taşıyan yüzeyler aynı kuralı paylaşır.
 */
import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    (element) => element.getAttribute('aria-hidden') !== 'true' && element.tabIndex !== -1,
  );
}

function trapTab(event: KeyboardEvent, container: HTMLElement): void {
  const focusable = focusableIn(container);
  if (focusable.length === 0) {
    event.preventDefault();
    container.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (first === undefined || last === undefined) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function useModalFocus(
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  initialFocusRef?: RefObject<HTMLElement | null>,
  open = true,
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const container = containerRef.current;
    if (container === null) return;

    const focusInitial = (): void => {
      const target = initialFocusRef?.current ?? focusableIn(container)[0] ?? container;
      target.focus();
    };
    const frame = requestAnimationFrame(focusInitial);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key === 'Tab') trapTab(event, container);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement && previous.isConnected) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [containerRef, initialFocusRef, open]);
}
